import { supabase } from './supabase';

export interface ProposalTemplateOffer {
  title: string;
  deposit_percentage?: number | null;
  save_card_on_deposit?: boolean | null;
  show_customer_address?: boolean | null;
  services: {
    service_id: string;
    vehicle_id: string | null;
    name: string;
    description: string;
    price: number;
    discountType?: 'percentage' | 'dollar';
    discountValue?: number;
    duration_minutes: number;
    isSelectable?: boolean;
    pricing_type?: 'flat' | 'per_unit' | 'per_sqft';
    unit_price?: number;
    duration_per_unit?: number;
    quantity?: number;
  }[];
  products: {
    stock_item_id: string;
    vehicle_id: string | null;
    name: string;
    price: number;
    quantity: number;
    category_name: string;
    discountType?: 'percentage' | 'dollar';
    discountValue?: number;
  }[];
}

export interface ProposalTemplateData {
  title?: string;
  expiryOption?: string;
  offers: ProposalTemplateOffer[];
}

export interface ProposalTemplate {
  id: string;
  business_id: string;
  name: string;
  template_data: ProposalTemplateData;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export async function getProposalTemplates(businessId: string): Promise<ProposalTemplate[]> {
  const { data, error } = await supabase
    .from('proposal_templates')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching proposal templates:', error);
    throw error;
  }

  return data || [];
}

export async function createProposalTemplate(
  businessId: string,
  name: string,
  templateData: ProposalTemplateData
): Promise<ProposalTemplate> {
  const { data: { user } } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('proposal_templates')
    .insert({
      business_id: businessId,
      name: name.trim(),
      template_data: templateData,
      created_by: user?.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating proposal template:', error);
    throw error;
  }

  return data;
}

export async function updateProposalTemplate(
  templateId: string,
  updates: { name?: string; template_data?: ProposalTemplateData }
): Promise<ProposalTemplate> {
  const updateData: Record<string, unknown> = {};

  if (updates.name !== undefined) updateData.name = updates.name.trim();
  if (updates.template_data !== undefined) updateData.template_data = updates.template_data;

  const { data, error } = await supabase
    .from('proposal_templates')
    .update(updateData)
    .eq('id', templateId)
    .select()
    .single();

  if (error) {
    console.error('Error updating proposal template:', error);
    throw error;
  }

  return data;
}

export async function deleteProposalTemplate(templateId: string): Promise<void> {
  const { error } = await supabase
    .from('proposal_templates')
    .delete()
    .eq('id', templateId);

  if (error) {
    console.error('Error deleting proposal template:', error);
    throw error;
  }
}
